/** Count modes for a data grid — the one implementation both `ResourceDataGrid`
 *  and the raw `DataGridPro` pages (bookings, orders, delivery logs, api
 *  requests) consume.
 *
 *  Why this exists: every grid used to render `rowCount={data.count}`, so every
 *  page request blocked on a `COUNT(*)`. On the big tables that count is most of
 *  the latency and nobody reads the number. See
 *  `backend/apiv2/lib/counting.py` for the server half.
 *
 *  MUI X gives us most of the UX (v7 `rowCount={-1}` +
 *  `paginationMeta={{ hasNextPage }}`): the pager reads "1–25 of more than 25",
 *  First/Prev/Next keep working, and no page count is computed.
 *
 *  What it does NOT give us — despite `useGridRowCount` appearing to — is the
 *  exact total on the last page: see `derivedTotal` below. We compute it, which
 *  is also what makes Next disable at the end. So paging to the end fills the
 *  total in for free and the user never has to click anything.
 *
 *  Three modes:
 *  - `sync`     — exact count on every request (the old behaviour);
 *  - `async`    — count computed off-request in Celery, polled here;
 *  - `deferred` — no count at all until `requestTotal()` is called.
 *
 *  The *default* mode is the backend view's business (that is what makes a cold
 *  first load free); `param` is only set when the user has overridden it, and
 *  `mode` otherwise follows what the response says actually ran.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  COUNT_UNKNOWN,
  countStatus,
  type CountMode,
  type CountOnlyResult,
  type CountState,
} from '@/api/countStatus';
import type { GridCountModeHandle } from '@/hooks/useUserPreferences';

export type { CountMode };

/** What the UI needs to know about the total right now. */
export type CountDisplayState =
  /** An exact number is available. */
  | 'exact'
  /** Not known and nothing in flight — deferred mode, awaiting a click. */
  | 'unknown'
  /** Being computed; a spinner is warranted. */
  | 'pending'
  /** Could not be computed (query too slow). Offer a retry, don't spin. */
  | 'unavailable';

/** The subset of a paginated list response this hook reads. */
export interface CountModeResponse {
  count?: number;
  count_mode?: CountMode;
  has_next?: boolean;
  async_count_handle?: string | null;
}

export interface UseCountModeOptions {
  /** The persisted override, from `useGridCountMode(gridCodename)`.
   *
   *  Passed in rather than read here on purpose: the override has to be known
   *  *before* the list params are built (it belongs in the query key, so
   *  switching modes re-issues the query), while everything else this hook does
   *  needs the response that query produces. Reading the preference in both
   *  places would mean two copies of the same state. So the caller reads it
   *  once, uses `pref.mode` for the request, and hands the handle here. */
  pref: GridCountModeHandle;
  /** Filter/search/ordering identity. A change resets any resolved total. */
  queryIdentity: string;
  /** The current list response (undefined while loading). */
  response: CountModeResponse | undefined;
  /** Issues `?count_only=1&count_mode=async` for the CURRENT query. Omit on
   *  grids that don't support on-demand totals; `requestTotal` then no-ops. */
  countOnly?: () => Promise<CountOnlyResult>;
  /** Current page (1-based). Pair with `rowsOnPage` + `onResetPage` to recover
   *  from an out-of-range page (see the effect below). */
  page?: number;
  /** Number of rows the current response actually returned. */
  rowsOnPage?: number;
  /** Current page size. With `page` + `rowsOnPage` this is what lets the exact
   *  total be derived on the last page without any COUNT. */
  pageSize?: number;
  onResetPage?: () => void;
}

export interface UseCountModeResult {
  /** Effective mode: the user's override, else whatever the server reported. */
  mode: CountMode;
  /** Send as `count_mode`; undefined when the user hasn't overridden anything. */
  param: CountMode | undefined;
  setMode: (mode: CountMode) => void;
  /** `rowCount` for the grid: an exact total, or -1 for "unknown". */
  rowCount: number;
  /** `paginationMeta` for the grid; undefined in sync mode. */
  paginationMeta: { hasNextPage: boolean } | undefined;
  /** The exact total when known, else null — for SelectAllBanner / batch. */
  total: number | null;
  state: CountDisplayState;
  /** Ask the backend for the total (the "Show total" click). */
  requestTotal: () => void;
  /** True while a count is being computed. */
  isResolving: boolean;
}

/** Poll cadence: 1s while a count is likely to land soon, then back off. */
const POLL_FAST_MS = 1_000;
const POLL_SLOW_MS = 2_000;
const POLL_FAST_ATTEMPTS = 10;
/** Stop polling after ~60s.
 *
 *  Not optional. The dashboard's equivalent loop (`RecordListPro.js`) has no
 *  give-up, so a handle that never resolves spins forever — which is exactly
 *  what a Redis-alias mismatch mid-deploy, or a dead Celery worker, produces.
 *  Giving up and offering a retry is the difference between a degraded total
 *  and a grid that looks broken.
 */
const POLL_GIVE_UP_MS = 60_000;

export function useCountMode({
  pref,
  queryIdentity,
  response,
  countOnly,
  page,
  rowsOnPage,
  pageSize,
  onResetPage,
}: UseCountModeOptions): UseCountModeResult {
  const { mode: override, setMode: persistMode } = pref;

  // What the server said it did. Until the first response lands we assume sync,
  // which is the safe default: it only affects which affordance is rendered,
  // and an exact count renders fine either way.
  const serverMode = response?.count_mode;
  const mode: CountMode = override ?? serverMode ?? 'sync';

  const [resolved, setResolved] = useState<number | null>(null);
  const [failed, setFailed] = useState(false);
  const [requested, setRequested] = useState(false);
  const [pendingHandle, setPendingHandle] = useState<string | null>(null);

  // A total for the previous filter set is not a total for this one. Mirrors the
  // extended-timeout reset in ResourceDataGrid: consenting once is not
  // consenting forever.
  const lastIdentity = useRef(queryIdentity);
  useEffect(() => {
    if (lastIdentity.current === queryIdentity) return;
    lastIdentity.current = queryIdentity;
    setResolved(null);
    setFailed(false);
    setRequested(false);
    setPendingHandle(null);
  }, [queryIdentity]);

  // An exact count can also arrive inline: sync mode always, and async mode once
  // the cached value is warm.
  const inlineCount =
    typeof response?.count === 'number' && response.count >= 0 ? response.count : null;

  // In async mode the list response itself carries the handle; in deferred mode
  // only an explicit requestTotal() produces one. Nothing to poll when the count
  // already came inline — and the backend re-signs the handle (timestamped) on
  // every response, so without this check each refetch of an unchanged query
  // (Refresh, auto-refresh) looked like a new handle and cost a /count_status/
  // round trip for a number the grid already had.
  const listHandle = response?.async_count_handle ?? null;
  useEffect(() => {
    if (mode !== 'async' || !listHandle || inlineCount !== null) return;
    setPendingHandle((current) => current ?? listHandle);
  }, [mode, listHandle, inlineCount]);

  // Poll loop.
  useEffect(() => {
    if (!pendingHandle) return;
    let cancelled = false;
    let attempts = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const startedAt = Date.now();

    const tick = async () => {
      try {
        const status = await countStatus(pendingHandle);
        if (cancelled) return;
        if (status.state === 'ready' || status.state === 'stale') {
          setResolved(status.count);
          setPendingHandle(null);
          return;
        }
        if (status.state === 'failed') {
          setFailed(true);
          setPendingHandle(null);
          return;
        }
      } catch {
        if (cancelled) return;
        setFailed(true);
        setPendingHandle(null);
        return;
      }
      if (Date.now() - startedAt >= POLL_GIVE_UP_MS) {
        setFailed(true);
        setPendingHandle(null);
        return;
      }
      attempts += 1;
      timer = setTimeout(tick, attempts < POLL_FAST_ATTEMPTS ? POLL_FAST_MS : POLL_SLOW_MS);
    };

    void tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [pendingHandle]);

  const requestTotal = useCallback(() => {
    if (!countOnly || pendingHandle) return;
    setRequested(true);
    setFailed(false);
    void countOnly()
      .then((result) => {
        if (result.count >= 0) {
          setResolved(result.count);
        } else if (result.async_count_handle) {
          setPendingHandle(result.async_count_handle);
        } else {
          setFailed(true);
        }
      })
      .catch(() => setFailed(true));
  }, [countOnly, pendingHandle]);

  const setMode = useCallback(
    (next: CountMode) => {
      // Switching modes invalidates whatever we knew: sync will re-count,
      // async/deferred start from "unknown".
      setResolved(null);
      setFailed(false);
      setRequested(false);
      setPendingHandle(null);
      persistMode(next);
    },
    [persistMode],
  );

  const hasNext = response?.has_next;
  const isSync = mode === 'sync';

  // The free total: on the last page the answer is arithmetic, no COUNT needed.
  //
  // MUI does NOT do this for you, despite `useGridRowCount` containing what
  // looks like exactly this logic. Its `hasNextPage === false` effect is
  // immediately undone by the sibling effect that re-applies `props.rowCount`
  // (-1) whenever the visible row count changes, so the grid never settles on a
  // derived value — verified against @mui/x-data-grid-pro 7.29 with a bare
  // DataGridPro: the pager stays on "more than 25" and Next stays enabled
  // forever. Deriving it here is also what makes Next disable correctly, since
  // that comes off a real `rowCount`.
  const derivedTotal = useMemo(() => {
    if (isSync || hasNext !== false) return null;
    if (rowsOnPage === undefined || pageSize === undefined) return null;
    const currentPage = page ?? 1;
    // An out-of-range page (0 rows past page 1) would derive a total that
    // overstates by a whole page. That case is bounced back to page 1 below.
    if (rowsOnPage === 0 && currentPage > 1) return null;
    return pageSize * (currentPage - 1) + rowsOnPage;
  }, [isSync, hasNext, rowsOnPage, pageSize, page]);

  const total = inlineCount ?? resolved ?? derivedTotal;

  // Out-of-range page: past page 1, zero rows, no next page. Only reachable from
  // a stale URL (these modes have no jump-to-page control), but MUI would
  // otherwise derive a *wrong* total from `hasNextPage === false` on an empty
  // page — so bounce back to page 1 rather than display a fabricated number.
  useEffect(() => {
    if (isSync || !onResetPage) return;
    if ((page ?? 1) > 1 && hasNext === false && rowsOnPage === 0) onResetPage();
  }, [isSync, onResetPage, page, hasNext, rowsOnPage]);

  const state: CountDisplayState = useMemo(() => {
    if (total !== null) return 'exact';
    if (pendingHandle) return 'pending';
    if (failed) return 'unavailable';
    return 'unknown';
  }, [total, pendingHandle, failed]);

  // MUST be referentially stable. MUI syncs `paginationMeta` into grid state in
  // an effect keyed on the prop itself and bails out on `===`, so a fresh object
  // every render re-sets the state every render — which both thrashes and stops
  // `useGridRowCount` from ever settling on the exact rowCount it derives when
  // `hasNextPage` turns false. A new object each render silently costs you the
  // free last-page total AND leaves Next permanently enabled.
  const paginationMeta = useMemo(
    () => (isSync || hasNext === undefined ? undefined : { hasNextPage: hasNext }),
    [isSync, hasNext],
  );

  return {
    mode,
    param: override ?? undefined,
    setMode,
    rowCount: total ?? COUNT_UNKNOWN,
    paginationMeta,
    total,
    state,
    requestTotal,
    isResolving: pendingHandle !== null || (requested && total === null && !failed),
  };
}

/** Human-readable mode labels, shared by the toolbar menu and help copy. */
export const COUNT_MODE_LABELS: Record<CountMode, { label: string; hint: string }> = {
  sync: {
    label: 'Instant',
    hint: 'Exact total on every page. Slowest on large tables.',
  },
  async: {
    label: 'In background',
    hint: 'Pages load immediately; the total appears when it is ready.',
  },
  deferred: {
    label: 'On demand',
    hint: 'Fastest. The total is only calculated when you ask for it.',
  },
};

export type { CountState };
